/**
 * Barra de estado del negocio — vive en el header, en todas las pantallas.
 *
 * POR QUE ACA Y NO EN UNA COLUMNA DEL MOSTRADOR
 * ---------------------------------------------
 * Antes esto era una columna de 288px al costado de la venta. Ocupaba ancho
 * permanente justo en la pantalla que mas lo necesita, para responder preguntas
 * que se hacen tres veces por dia — no treinta, como "cobrame esto". Aca ocupa
 * una franja del header que ya estaba vacia, y la venta se queda con todo.
 *
 * Ademas la cotizacion es GLOBAL: la usa cada pantalla que muestre plata, y se
 * tocaba unicamente desde el inicio. Ahora esta donde alcanza a lo que gobierna.
 *
 * Aca hubo tambien una pastilla de "para reponer", otra con la caja abreviada
 * y el ojito de discrecion. Se sacaron el 2026-09-30: no se usaban.
 *
 * Ojo con lo que promete el interruptor: apaga el margen del mostrador (la
 * pantalla que ve el cliente) y la caja de esta barra, nada mas. Ganancias,
 * Socios y Caja siguen a la vista. Antes el texto decia «ocultar margenes y
 * totales» en general, y alguien lo apagaba en Ganancias creyendo que la
 * pantalla quedaba a resguardo.
 *
 * LA COTIZACION SALE DEL BACKEND. NO SE NEGOCIA.
 * ----------------------------------------------
 * Hubo una cotizacion compacta en este mismo header y se saco, con razon: leia
 * del estado local de `MonedaProvider`, que nunca se conecto, y mostraba
 * "$ 1,0000" con el dolar a 1550. Una cotizacion incorrecta en pantalla es peor
 * que ninguna, porque alguien le cotiza a un cliente con ese numero.
 *
 * Esta lee `useConfig()` — la misma fuente que usaba la tarjeta del mostrador,
 * que es la que efectivamente andaba. Si algun dia se toca, que siga saliendo
 * de ahi.
 *
 * POR QUE LOS CONTADORES PIERDEN LA ETIQUETA
 * ------------------------------------------
 * En la columna cada numero venia con su texto ("7 clientes en mora"). Aca no
 * entra, y queda el icono con el numero. Es el costo del cambio y se paga con
 * tooltip + `aria-label`: el lector de pantalla escucha la frase completa, y
 * el que mira aprende los iconos en un dia. Lo que NO se toca es el
 * color: el ambar sigue queriendo decir "esto reclama algo".
 */
import { Link } from "@tanstack/react-router";
import { AlertTriangle, RefreshCw, Wrench, type LucideIcon } from "lucide-react";

import { formatear, formatearCotizacion, formatearCotizacionNumero } from "@tora/shared";
import { IconButton } from "@/components/ui/icon-button";
import { Avisos } from "./Avisos";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { useConfig, useDeudas, useSincronizarCotizacion, useTableroTaller } from "@/lib/queries";
import { cn } from "@/lib/utils";

/** Un contador con su ruta. El ambar solo enciende si hay algo que atender. */
function Pill({
  to,
  search,
  icono: Icono,
  valor,
  etiqueta,
  detalle,
  alerta = false,
  compacto = false,
}: {
  to: string;
  /** La solapa de destino, cuando la pantalla tiene varias. */
  search?: Record<string, string>;
  icono: LucideIcon;
  valor: string;
  etiqueta: string;
  /** Lo que se lee en el tooltip debajo de la etiqueta: el valor exacto, o una nota. */
  detalle?: string;
  alerta?: boolean;
  /** Sin borde y angosto: el encabezado del celular tiene un solo renglón. */
  compacto?: boolean;
}): JSX.Element {
  const enciende = alerta && valor !== "0";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={to as never}
          search={search as never}
          aria-label={`${valor} ${etiqueta}`}
          className={cn(
            // 44px de alto en el celular (objetivo tactil) y 36 en desktop, que
            // es la altura que comparte con el boton de buscar en la misma linea.
            "inline-flex h-11 shrink-0 items-center gap-1.5 rounded-lg px-2 transition-colors duration-150 sm:h-9",
            compacto ? "px-1.5" : "border",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            "[&_svg]:size-3.5 [&_svg]:shrink-0",
            enciende
              ? "border-warning text-warning hover:bg-accent"
              : "border-border text-muted-foreground hover:bg-accent hover:text-foreground",
          )}
        >
          <Icono aria-hidden="true" />
          <span className="text-sm font-semibold tabular-nums">{valor}</span>
        </Link>
      </TooltipTrigger>
      <TooltipContent>
        <span className="capitalize">{etiqueta}</span>
        {detalle && <span className="block text-muted-foreground">{detalle}</span>}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * `compacto`: la versión del encabezado del celular, que ahora es un solo
 * renglón. El dólar va solo con su número (tocarlo lleva a Configuración,
 * donde se actualiza o se carga a mano) y los contadores sin borde. Antes la
 * barra ocupaba un renglón propio y el encabezado se comía 115 px de 844.
 */
export function BarraEstado({
  className,
  compacto = false,
}: {
  className?: string;
  compacto?: boolean;
}): JSX.Element {
  const { data: config } = useConfig();
  const { data: deudas } = useDeudas();
  const { data: taller } = useTableroTaller();
  const sincronizar = useSincronizarCotizacion();

  const cotizacion = Number(config?.cotizacion_vigente ?? 0);
  const auto = config?.config?.cotizacion_modo === "AUTO";

  const enMora = (deudas?.filas ?? []).length;
  const enTaller = (taller?.tablero ?? []).reduce(
    (a: number, t: any) => a + Number(t.ordenes ?? 0),
    0,
  );
  const listos = (taller?.sin_retirar ?? []).length;

  if (compacto) {
    return (
      <div className={cn("flex items-center", className)}>
        <Link
          to="/configuracion"
          aria-label={`Dólar a ${cotizacion > 0 ? formatearCotizacion(cotizacion) : "sin cargar"}, ${auto ? "se actualiza solo" : "cargado a mano"}. Ir a Configuración`}
          className="inline-flex h-11 shrink-0 items-center gap-1 rounded-lg px-1.5 text-xs text-muted-foreground transition-colors duration-150 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          US$
          <span className="text-sm font-semibold tabular-nums text-foreground">
            {cotizacion > 0 ? formatearCotizacionNumero(cotizacion) : "—"}
          </span>
        </Link>
        <Pill
          to="/clientes"
          icono={AlertTriangle}
          valor={String(enMora)}
          etiqueta={enMora === 1 ? "cliente en mora" : "clientes en mora"}
          alerta={enMora > 0}
          compacto
        />
        <Pill
          to="/reparaciones"
          icono={Wrench}
          valor={String(enTaller)}
          etiqueta={enTaller === 1 ? "equipo en taller" : "equipos en taller"}
          detalle={listos > 0 ? `${listos} sin retirar` : undefined}
          compacto
        />
        <Avisos />
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* ── Dolar ── */}
      <div className="flex h-11 shrink-0 items-center gap-2 rounded-lg border border-border px-2 sm:h-9">
        <TituloSeccion nivel="etiqueta" as="span">
          Dólar
        </TituloSeccion>
        <span className="text-sm font-semibold tabular-nums text-foreground">
          {cotizacion > 0 ? formatearCotizacion(cotizacion) : "—"}
        </span>

        {/*
          El modo no es decorativo: es un link a donde se cambia. Antes eso era
          un "cargar a mano" escrito aparte; aca la palabra que dice como esta
          es la misma que lleva a cambiarlo.
        */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to="/configuracion"
              className="rounded-sm text-xs text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {auto ? "auto" : "manual"}
            </Link>
          </TooltipTrigger>
          <TooltipContent>
            {auto ? "Se actualiza sola. Tocá para cargarla a mano." : "Cargada a mano."}
            <span className="block text-muted-foreground">Ir a Configuración</span>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <IconButton
              icono={RefreshCw}
              tamano="sm"
              aria-label="Actualizar cotización"
              cargando={sincronizar.isPending}
              className="-mr-1 text-muted-foreground hover:text-foreground"
              onClick={() =>
                sincronizar.mutate(undefined, {
                  onSuccess: (r: any) =>
                    mostrarExito(
                      `Dólar actualizado: ${formatear(Number(r?.cotizacion_vigente ?? 0), "ARS")}`,
                      "Los precios del catálogo se recalcularon solos.",
                    ),
                  onError: mostrarError,
                })
              }
            />
          </TooltipTrigger>
          <TooltipContent>
            {sincronizar.isPending ? "Buscando…" : "Actualizar cotización"}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* ── Pendientes ── */}
      <Pill
        to="/clientes"
        icono={AlertTriangle}
        valor={String(enMora)}
        etiqueta={enMora === 1 ? "cliente en mora" : "clientes en mora"}
        alerta={enMora > 0}
      />
      <Pill
        to="/reparaciones"
        icono={Wrench}
        valor={String(enTaller)}
        etiqueta={enTaller === 1 ? "equipo en taller" : "equipos en taller"}
        detalle={listos > 0 ? `${listos} sin retirar` : undefined}
      />

      {/* ── Datos para revisar ── */}
      <Avisos />
    </div>
  );
}
